import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useLocaleState, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { useFunnelStages, useStage } from "../funnel/hooks";
import { FunnelShell } from "../funnel/shared";
import type { CrmDataProvider } from "../providers/types";
import { PERIODS, percent, periodStart, type Period } from "./dashboardLogic";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useTags } from "../tags/useTags";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  Clock,
  KeyRound,
  PhoneMissed,
  UserX,
} from "lucide-react";

type Kpi = {
  leads: number;
  fast: number;
  viewings: number;
  sales: number;
  sales_amount: number;
};
type Overview = {
  current: Kpi;
  previous: Kpi | null;
  daily: { day: string; facebook: number; instagram: number; other: number }[];
  attention: {
    new_no_call: number;
    old_no_call: number;
    unassigned: number;
    overdue_tasks: number;
    bookings_ending: number;
  };
  apartments: Partial<
    Record<
      "free" | "booked" | "sold" | "off",
      { count: number; amount: number }
    >
  >;
  managers: {
    id: number;
    name: string;
    leads: number;
    fast: number;
    viewings: number;
    sales: number;
    no_call: number;
  }[];
};

const SOURCE_COLORS = {
  facebook: "#2f5d46",
  instagram: "#d89a2b",
  other: "#9db4c8",
} as const;

/** Owner dashboard: the headline numbers, leads per day, what needs attention,
 * apartments in money, the managers; funnel and sources folded below */
export const OwnerDashboard = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const stages = useFunnelStages();
  const stage = useStage();
  const { currency, dashboard: settings = {} } = useConfigurationContext();
  const [locale] = useLocaleState();
  const [period, setPeriod] = useState<Period>(
    settings.defaultPeriod ?? "week",
  );
  const { data: tags = [] } = useTags();
  const oldTag =
    tags.find((tag) => tag.id === settings.oldTagId) ??
    (settings.oldTagId === undefined
      ? tags.find((tag) => tag.name === "Старий лід")
      : undefined);
  const fastMinutes = settings.fastMinutes ?? 15;
  const bookingDays = settings.bookingDays ?? 3;
  const show = (
    key: "showDaily" | "showAttention" | "showApartments" | "showManagers",
  ) => settings[key] !== false;
  const stageValues = stages.map((s) => s.value);
  const since = periodStart(period, new Date());
  const days = period === "week" ? 14 : 30;
  const { data: overview } = useQuery({
    queryKey: ["owner_overview", period, oldTag?.id, fastMinutes, bookingDays],
    queryFn: async () => {
      const now = Date.now();
      const length = period === "week" ? 7 : 30;
      const { data, error } = await getSupabaseClient().rpc("owner_overview", {
        since,
        prev_since:
          period === "all"
            ? null
            : new Date(now - 2 * length * 24 * 3600_000).toISOString(),
        days,
        old_tag: oldTag?.id ?? null,
        fast_minutes: fastMinutes,
        booking_days: bookingDays,
      });
      if (error) throw error;
      return data as Overview;
    },
    refetchInterval: 60_000,
  });
  const [showDetails, setShowDetails] = useState(false);
  const { data } = useQuery({
    queryKey: ["owner_dashboard", period, stageValues],
    queryFn: () => dataProvider.ownerDashboard(since, stageValues),
    enabled: showDetails,
  });

  const money = (value: number) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      notation: value >= 1_000_000 ? "compact" : "standard",
      maximumFractionDigits: value >= 1_000_000 ? 2 : 0,
    }).format(value);

  const periodTabs = (
    <div
      role="tablist"
      className={cn(
        "grid grid-cols-3 rounded-xl border bg-card p-1 text-sm",
        isMobile ? "w-full" : "ml-auto",
      )}
    >
      {PERIODS.map((value) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={period === value}
          onClick={() => setPeriod(value)}
          className={cn(
            "rounded-lg px-4 py-1.5 font-semibold",
            period === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground",
          )}
        >
          {translate(`crm.owner_dashboard.periods.${value}`)}
        </button>
      ))}
    </div>
  );

  const cur = overview?.current;
  const prev = overview?.previous;
  const fastShare = cur ? percent(cur.fast, cur.leads) : null;
  const prevFastShare = prev ? percent(prev.fast, prev.leads) : null;
  const kpis: {
    key: string;
    value: string;
    sub?: string;
    now?: number | null;
    before?: number | null;
    points?: boolean;
  }[] = [
    {
      key: "leads",
      value: String(cur?.leads ?? "—"),
      now: cur?.leads,
      before: prev?.leads,
    },
    {
      key: "fast",
      value: fastShare == null ? "—" : `${fastShare}%`,
      sub: cur
        ? translate("crm.owner_overview.fast_sub", {
            fast: cur.fast,
            leads: cur.leads,
          })
        : undefined,
      now: fastShare,
      before: prevFastShare,
      points: true,
    },
    {
      key: "viewings",
      value: String(cur?.viewings ?? "—"),
      now: cur?.viewings,
      before: prev?.viewings,
    },
    {
      key: "sales",
      value: String(cur?.sales ?? "—"),
      sub: cur?.sales ? money(cur.sales_amount) : undefined,
      now: cur?.sales,
      before: prev?.sales,
    },
  ];

  const daily = overview?.daily ?? [];
  const dayMax = Math.max(
    1,
    ...daily.map((d) => d.facebook + d.instagram + d.other),
  );
  const dayTotal = daily.reduce(
    (sum, d) => sum + d.facebook + d.instagram + d.other,
    0,
  );

  const att = overview?.attention;
  const attention = att
    ? (
        [
          {
            key: "new_no_call",
            count: att.new_no_call,
            to: "/today",
            icon: PhoneMissed,
            tone: "text-destructive bg-destructive/10",
          },
          {
            key: "overdue_tasks",
            count: att.overdue_tasks,
            to: "/tasks",
            icon: Clock,
            tone: "text-[#a8620f] bg-[#fbefd2]",
          },
          {
            key: "bookings_ending",
            count: att.bookings_ending,
            to: "/apartments",
            icon: KeyRound,
            tone: "text-[#a8620f] bg-[#fbefd2]",
          },
          {
            key: "unassigned",
            count: att.unassigned,
            to: "/funnel",
            icon: UserX,
            tone: "text-primary bg-accent",
          },
          {
            key: "old_no_call",
            count: att.old_no_call,
            to: oldTag ? `/funnel?tag=${oldTag.id}` : "/funnel",
            icon: AlertTriangle,
            tone: "text-muted-foreground bg-muted",
          },
        ] as const
      ).filter((row) => row.count > 0)
    : [];

  const apt = overview?.apartments ?? {};
  const totalAmount = (["free", "booked", "sold"] as const).reduce(
    (sum, k) => sum + (apt[k]?.amount ?? 0),
    0,
  );
  const top = data?.funnel[0]?.count ?? 0;
  const share = (k: "free" | "booked" | "sold") =>
    totalAmount ? (100 * (apt[k]?.amount ?? 0)) / totalAmount : 0;

  return (
    <FunnelShell title={translate("ra.page.dashboard")} actions={periodTabs}>
      {/* 1. headline numbers against the previous period */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((kpi) => {
          const delta =
            kpi.now != null && kpi.before != null && period !== "all"
              ? kpi.points
                ? kpi.now - kpi.before
                : kpi.before > 0
                  ? Math.round((100 * (kpi.now - kpi.before)) / kpi.before)
                  : null
              : null;
          return (
            <div key={kpi.key} className="rounded-2xl border bg-card p-4">
              <div className="text-sm text-muted-foreground">
                {translate(`crm.owner_overview.kpi.${kpi.key}`, {
                  minutes: fastMinutes,
                })}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight tabular-nums sm:text-4xl">
                  {kpi.value}
                </span>
                {delta != null && delta !== 0 ? (
                  <span
                    className={cn(
                      "inline-flex items-center text-sm font-bold",
                      delta > 0 ? "text-[#17703c]" : "text-destructive",
                    )}
                  >
                    {delta > 0 ? (
                      <ArrowUpRight className="size-4" />
                    ) : (
                      <ArrowDownRight className="size-4" />
                    )}
                    {Math.abs(delta)}
                    {kpi.points ? " п.п." : "%"}
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-muted-foreground">
                {kpi.sub ??
                  (period === "all"
                    ? translate("crm.owner_overview.all_time")
                    : translate("crm.owner_overview.vs_prev", {
                        value: kpi.before ?? "—",
                      }))}
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={cn(
          "grid gap-4",
          show("showDaily") &&
            show("showAttention") &&
            "lg:grid-cols-[1fr_380px]",
        )}
      >
        {/* 2. leads per day by source */}
        {show("showDaily") ? (
          <section className="flex min-w-0 flex-col rounded-2xl border bg-card p-5">
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-bold">
                {translate("crm.owner_overview.daily", { days })}
              </h2>
              <span className="text-sm text-muted-foreground">
                {translate("crm.owner_overview.daily_total", {
                  count: dayTotal,
                })}
              </span>
            </div>
            <div className="flex h-40 items-end gap-[3px] sm:gap-1.5 lg:h-auto lg:min-h-48 lg:flex-1">
              {daily.map((d) => {
                const total = d.facebook + d.instagram + d.other;
                const date = new Date(d.day);
                return (
                  <div
                    key={d.day}
                    className="group relative flex h-full flex-1 flex-col justify-end"
                    title={`${date.toLocaleDateString(locale, { day: "numeric", month: "short" })}: ${total}`}
                  >
                    {total ? (
                      <span className="mb-0.5 text-center text-[10px] font-bold text-muted-foreground tabular-nums">
                        {total}
                      </span>
                    ) : null}
                    <div
                      className="flex flex-col-reverse overflow-hidden rounded-md bg-muted"
                      style={{
                        height: `${Math.max(3, (100 * total) / dayMax)}%`,
                      }}
                    >
                      {(["facebook", "instagram", "other"] as const).map((k) =>
                        d[k] ? (
                          <div
                            key={k}
                            style={{
                              height: `${(100 * d[k]) / (total || 1)}%`,
                              backgroundColor: SOURCE_COLORS[k],
                            }}
                          />
                        ) : null,
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-1 flex gap-[3px] text-center text-[10px] text-muted-foreground sm:gap-1.5">
              {daily.map((d, i) => (
                <span key={d.day} className="flex-1 tabular-nums">
                  {i % (isMobile || days > 14 ? 3 : 1) === 0
                    ? new Date(d.day).getDate()
                    : ""}
                </span>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
              {(["facebook", "instagram", "other"] as const).map((k) => (
                <span key={k} className="inline-flex items-center gap-1.5">
                  <i
                    className="size-2.5 rounded-sm"
                    style={{ backgroundColor: SOURCE_COLORS[k] }}
                  />
                  {translate(`crm.owner_overview.sources.${k}`)}
                </span>
              ))}
            </div>
          </section>
        ) : null}

        {/* 3. what needs attention right now */}
        {show("showAttention") ? (
          <section className="rounded-2xl border bg-card p-5">
            <h2 className="mb-3 text-lg font-bold">
              {translate("crm.owner_overview.attention")}
            </h2>
            {attention.length ? (
              <ul className="flex flex-col gap-2">
                {attention.map((row) => (
                  <li key={row.key}>
                    <Link
                      to={row.to}
                      className="flex items-center gap-3 rounded-xl border px-3 py-2.5 text-foreground no-underline transition hover:border-primary/40 hover:bg-accent/40"
                    >
                      <span
                        className={cn(
                          "flex size-9 shrink-0 items-center justify-center rounded-lg",
                          row.tone,
                        )}
                      >
                        <row.icon className="size-4.5" />
                      </span>
                      <span className="flex-1 text-sm font-semibold leading-tight">
                        {translate(`crm.owner_overview.att.${row.key}`, {
                          days: bookingDays,
                        })}
                      </span>
                      <span className="text-xl font-extrabold tabular-nums">
                        {row.count}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="rounded-xl bg-accent/50 p-4 text-sm font-semibold text-primary">
                {translate("crm.owner_overview.all_good")}
              </p>
            )}
          </section>
        ) : null}
      </div>

      <div
        className={cn(
          "grid gap-4",
          show("showApartments") &&
            show("showManagers") &&
            "lg:grid-cols-[380px_1fr]",
        )}
      >
        {/* 4. apartments in money */}
        {show("showApartments") ? (
          <section className="rounded-2xl border bg-card p-5">
            <h2 className="mb-1 text-lg font-bold">
              {translate("crm.owner_overview.apartments")}
            </h2>
            <div className="text-sm text-muted-foreground">
              {translate("crm.owner_overview.sold_of", {
                sold: money(apt.sold?.amount ?? 0),
                total: money(totalAmount),
              })}
            </div>
            <div className="my-4 flex h-4 overflow-hidden rounded-full bg-muted">
              <div
                className="bg-[#c45a4c]"
                style={{ width: `${share("sold")}%` }}
              />
              <div
                className="bg-[#e3ac45]"
                style={{ width: `${share("booked")}%` }}
              />
              <div
                className="bg-[#2f7a50]"
                style={{ width: `${share("free")}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ["sold", "#c45a4c"],
                  ["booked", "#e3ac45"],
                  ["free", "#2f7a50"],
                ] as const
              ).map(([k, color]) => (
                <Link
                  key={k}
                  to="/apartments"
                  className="rounded-xl bg-muted/60 p-2.5 text-foreground no-underline"
                >
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <i
                      className="size-2.5 rounded-sm"
                      style={{ backgroundColor: color }}
                    />
                    {translate(`crm.apartments.counts.${k}`)}
                  </div>
                  <div className="text-xl font-extrabold tabular-nums">
                    {apt[k]?.count ?? 0}
                  </div>
                  <div className="text-xs font-semibold text-muted-foreground tabular-nums">
                    {money(apt[k]?.amount ?? 0)}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {/* 5. managers */}
        {show("showManagers") ? (
          <section className="min-w-0 rounded-2xl border bg-card p-5">
            <h2 className="mb-3 text-lg font-bold">
              {translate("crm.owner_overview.managers")}
            </h2>
            {overview?.managers.length ? (
              isMobile ? (
                <ul className="flex flex-col gap-2">
                  {overview.managers.map((m) => (
                    <li key={m.id} className="rounded-xl border p-3">
                      <div className="font-bold">{m.name}</div>
                      <div className="mt-2 grid grid-cols-4 gap-1 text-center">
                        {[
                          [m.leads, "leads"],
                          [
                            percent(m.fast, m.leads) == null
                              ? "—"
                              : `${percent(m.fast, m.leads)}%`,
                            "fast",
                          ],
                          [m.viewings, "viewings"],
                          [m.sales, "sales"],
                        ].map(([value, key]) => (
                          <div key={key}>
                            <div className="text-lg font-extrabold tabular-nums">
                              {value}
                            </div>
                            <div className="text-[10px] leading-tight text-muted-foreground">
                              {translate(`crm.owner_overview.m.${key}`, {
                                minutes: fastMinutes,
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      {m.no_call ? (
                        <div className="mt-2 text-xs font-semibold text-destructive">
                          {translate("crm.owner_overview.m.no_call", {
                            count: m.no_call,
                          })}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="py-2 pr-3">
                        {translate("crm.owner_overview.m.name")}
                      </th>
                      {["leads", "fast", "viewings", "sales", "waiting"].map(
                        (key) => (
                          <th key={key} className="px-3 py-2 text-right">
                            {translate(`crm.owner_overview.m.${key}`, {
                              minutes: fastMinutes,
                            })}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {overview.managers.map((m) => {
                      const fast = percent(m.fast, m.leads);
                      return (
                        <tr key={m.id} className="border-b last:border-0">
                          <td className="py-2.5 pr-3 font-semibold">
                            {m.name}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums">
                            {m.leads}
                          </td>
                          <td
                            className={cn(
                              "px-3 py-2.5 text-right font-semibold tabular-nums",
                              fast == null
                                ? ""
                                : fast >= 70
                                  ? "text-[#17703c]"
                                  : fast < 40
                                    ? "text-destructive"
                                    : "",
                            )}
                          >
                            {fast == null ? "—" : `${fast}%`}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums">
                            {m.viewings}
                          </td>
                          <td className="px-3 py-2.5 text-right font-bold tabular-nums">
                            {m.sales}
                          </td>
                          <td
                            className={cn(
                              "px-3 py-2.5 text-right tabular-nums",
                              m.no_call && "font-bold text-destructive",
                            )}
                          >
                            {m.no_call}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            ) : (
              <p className="text-sm text-muted-foreground">
                {translate("crm.owner_overview.no_managers")}{" "}
                <Link
                  to="/sales/create"
                  className="font-semibold text-primary underline"
                >
                  {translate("crm.team.invite")}
                </Link>
              </p>
            )}
          </section>
        ) : null}
      </div>

      {/* 6. funnel and sources, folded */}
      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        className="inline-flex items-center gap-1.5 self-start rounded-xl border bg-card px-4 py-2 text-sm font-semibold"
      >
        <ChevronDown
          className={cn("size-4 transition", showDetails && "rotate-180")}
        />
        {translate("crm.owner_overview.details")}
      </button>
      {showDetails ? (
        <>
          <div className="grid gap-4">
            <section className="rounded-2xl border bg-card p-5">
              <h2 className="mb-4 text-lg font-bold">
                {translate("crm.owner_dashboard.funnel")}
              </h2>
              <div className="flex flex-col gap-3">
                {data?.funnel.map((row, index) => {
                  const s = stage(row.stage);
                  const previous =
                    index > 0 ? data.funnel[index - 1].count : null;
                  const share =
                    previous != null ? percent(row.count, previous) : null;
                  return (
                    <div
                      key={row.stage}
                      className="grid grid-cols-[88px_1fr_auto] items-center gap-3 text-sm sm:grid-cols-[100px_1fr_80px]"
                    >
                      <span className="truncate">{s?.label ?? row.stage}</span>
                      <div className="h-5 overflow-hidden rounded-md bg-muted">
                        <div
                          className="h-full rounded-md"
                          style={{
                            width: `${top ? Math.max(2, (100 * row.count) / top) : 0}%`,
                            backgroundColor: s?.color,
                          }}
                        />
                      </div>
                      <span className="text-right font-bold tabular-nums">
                        {row.count}
                        {share != null ? ` · ${share}%` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                {translate("crm.owner_dashboard.funnel_hint")}
              </p>
            </section>
          </div>

          {
            <section className="overflow-x-auto rounded-2xl border bg-card">
              <h2 className="px-5 pt-5 text-lg font-bold">
                {translate("crm.owner_dashboard.sources")}
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-5 py-3">
                      {translate("crm.owner_dashboard.source")}
                    </th>
                    <th className="px-5 py-3 text-right">
                      {translate("crm.owner_dashboard.source_leads")}
                    </th>
                    <th className="px-5 py-3 text-right">
                      {translate("crm.owner_dashboard.source_viewings")}
                    </th>
                    <th className="px-5 py-3 text-right">
                      {translate("crm.owner_dashboard.source_won")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.sources.map((row) => (
                    <tr key={row.source} className="border-b last:border-0">
                      <td className="px-5 py-2 font-semibold">
                        {translate(`resources.contacts.sources.${row.source}`)}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {row.leads}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {row.viewings}
                        {row.leads
                          ? ` · ${percent(row.viewings, row.leads)}%`
                          : ""}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {row.won}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          }
        </>
      ) : null}
    </FunnelShell>
  );
};
